import { SlatePlugin } from '@nucleus/lib-slate';
import { BaseEditor, Editor, Element, Point, Range, Text, Transforms } from 'slate';

/** Symbol used to detect whether the autoformat plugin is active on an editor instance. */
export const AutoFormatSymbol = Symbol('AutoFormat');

declare module '../types/custom-editor' {
  interface CustomEditor extends AutoFormatEditor {}
}

/** Extension to BaseEditor indicating the autoformat plugin is active. */
export interface AutoFormatEditor extends BaseEditor {
  [AutoFormatSymbol]: true;
}

/**
 * Defines the start and end delimiters for a paired match.
 *
 * For symmetric delimiters like `**`, start and end are the same string.
 * For asymmetric delimiters, they differ (e.g. `{ start: '_*', end: '*_' }`).
 */
export interface MatchRange {
  /** Opening delimiter (e.g. `**` for bold). */
  start: string;
  /** Closing delimiter (e.g. `**` for bold). */
  end: string;
}

/**
 * Options passed to a rule's {@link AutoFormatCommonRule.query} function.
 * Contains all rule properties except `query` itself, plus the typed character.
 */
export interface AutoFormatQueryOptions extends Omit<AutoFormatCommonRule, 'query'> {
  /** The character that was typed, triggering the autoformat check. */
  text: string;
}

/**
 * Properties shared by all autoformat rule types.
 *
 * Adapted from Plate's `AutoformatCommonRule` for use with vanilla Slate.
 */
export interface AutoFormatCommonRule {
  /**
   * Pattern(s) to match before the cursor.
   *
   * - `'block'` mode: looks for the end match before the cursor.
   * - `'text'` mode: looks for the end match before the cursor. If `format`
   *   is an array, also looks for a start match.
   * - `'mark'` mode: looks for both start and end matches.
   *
   * A string like `'_*'` is equivalent to `{ start: '_*', end: '*_' }` (the
   * string is reversed to derive the end pattern).
   */
  match: MatchRange | Array<MatchRange> | Array<string> | string;
  /**
   * If true, the triggering character is inserted after autoformatting.
   * @default false
   */
  insertTrigger?: boolean;
  /**
   * Predicate called before attempting the match. Return false to skip
   * this rule for the current editor state.
   */
  query?: (editor: Editor, options: AutoFormatQueryOptions) => boolean;
  /**
   * Character(s) that trigger the autoformat check.
   * @default the last character of `match` (or `match.end`)
   */
  trigger?: Array<string> | string;
}

/**
 * Rule that transforms the current block element when a pattern is typed.
 *
 * When the typed trigger character completes a match against text from the
 * block start (or cursor position), the matched text is deleted and either
 * a custom `format` function is called or the block type is set via `type`.
 *
 * @example
 * ```ts
 * { mode: 'block', match: '# ', type: 'heading-one' }
 * { mode: 'block', match: '- ', format: (editor) => toggleList(editor) }
 * ```
 */
export interface AutoFormatBlockRule extends AutoFormatCommonRule {
  mode: 'block';
  /** Pattern string(s) including the trigger as the last character. */
  match: Array<string> | string;
  /**
   * When false, skips the rule if a block of the same `type` already
   * exists at the current selection. Ignored when `type` is not set.
   * @default false
   */
  allowSameTypeAbove?: boolean;
  /**
   * Custom formatting function. Receives the matched text string.
   * When omitted, the block type is set to `type` via `Transforms.setNodes`.
   */
  format?: (editor: Editor, context: { matchString: string }) => void;
  /**
   * When true, the match pattern is treated as a regular expression
   * instead of a literal string comparison.
   * @default false
   */
  matchByRegex?: boolean;
  /**
   * Called before `format`, useful for resetting block state before
   * applying the new format.
   */
  preFormat?: (editor: Editor) => void;
  /**
   * When true, the match pattern is tested against the entire text from
   * the block start to the cursor. When false, the pattern is matched
   * against text immediately before the cursor (anchored to cursor, not
   * to block start).
   * @default true
   */
  triggerAtBlockStart?: boolean;
  /**
   * The block element type to set when no custom `format` is provided.
   * Also used for the `allowSameTypeAbove` guard.
   */
  type?: string;
}

/**
 * Rule that performs inline text replacement when a pattern is typed.
 *
 * Supports three `format` modes:
 * - **function**: called with matched points for custom logic (e.g. smart quotes).
 * - **string**: replaces the matched text with this string (e.g. `'...'` → `'…'`).
 * - **string[]**: `[formatStart, formatEnd]` — replaces the opening match with
 *   `formatStart` and inserts `formatEnd` at the cursor.
 *
 * @example
 * ```ts
 * { mode: 'text', match: '...', format: '…' }
 * { mode: 'text', match: '"', format: (editor) => insertSmartQuote(editor) }
 * ```
 */
export interface AutoFormatTextRule extends AutoFormatCommonRule {
  mode: 'text';
  /** Pattern string(s) including the trigger as the last character. */
  match: Array<string> | string;
  /**
   * - **function**: custom handler, receives matched delimiter points.
   * - **string**: the matched text is replaced by this string.
   * - **string[]**: `[formatStart, formatEnd]` — opening and closing
   *   replacements for pair-matched patterns.
   */
  format: ((editor: Editor, options: GetMatchPointsReturnType) => void) | Array<string> | string;
}

/**
 * Rule that applies marks (bold, italic, etc.) to text between matched
 * delimiters.
 *
 * When the closing delimiter is typed, the text between the opening and
 * closing delimiters receives the specified mark(s), and both delimiters
 * are removed.
 *
 * @example
 * ```ts
 * { mode: 'mark', match: '**', type: 'bold' }
 * { mode: 'mark', match: '***', type: ['bold', 'italic'] }
 * ```
 */
export interface AutoFormatMarkRule extends AutoFormatCommonRule {
  mode: 'mark';
  /** Mark key(s) to apply to text between the matched delimiters. */
  type: Array<string> | string;
  /**
   * When false (default), skips formatting if the text between delimiters
   * has leading or trailing whitespace. Set to true to allow marks on
   * untrimmed content.
   * @default false
   */
  ignoreTrim?: boolean;
}

/** Discriminated union of all autoformat rule types, keyed by `mode`. */
export type AutoFormatRule = AutoFormatBlockRule | AutoFormatMarkRule | AutoFormatTextRule;

/**
 * Points around the start and end delimiter matches, returned by
 * {@link getMatchPoints}.
 *
 * - `beforeStartMatchPoint`: point at the beginning of the start delimiter.
 * - `afterStartMatchPoint`: point at the end of the start delimiter.
 * - `beforeEndMatchPoint`: point at the beginning of the end delimiter.
 *
 * Returns `undefined` when no match is found.
 */
export type GetMatchPointsReturnType =
  | {
      afterStartMatchPoint: Point | undefined;
      beforeEndMatchPoint: Point;
      beforeStartMatchPoint: Point | undefined;
    }
  | undefined;

const toArray = <T>(value: T | Array<T>): Array<T> => (Array.isArray(value) ? value : [value]);

/**
 * Derives the trigger character(s) and start/end patterns from a match
 * definition.
 *
 * When `match` is a string, it is used as `start` and its reverse as `end`.
 * The trigger defaults to the last character of `end`, which is then stripped
 * from `end` so the trigger is not part of the pattern tested against editor
 * text. When an explicit `trigger` is provided, `end` is left intact.
 *
 * @example
 * ```
 * getMatchRange('**', undefined)
 * // → { start: '**', end: '*', triggers: ['*'] }
 *
 * getMatchRange({ end: '- ', start: '' }, undefined)
 * // → { start: '', end: '-', triggers: [' '] }
 * ```
 */
const getMatchRange = (
  match: MatchRange | string,
  trigger: Array<string> | string | undefined
): { start: string; end: string; triggers: Array<string> } => {
  let start: string;
  let end: string;

  if (typeof match === 'object') {
    start = match.start;
    end = match.end;
  } else {
    start = match;
    end = start.split('').reverse().join('');
  }

  const triggers: Array<string> = trigger !== undefined ? toArray(trigger) : [end.slice(-1)];
  end = trigger !== undefined ? end : end.slice(0, -1);

  return { start, end, triggers };
};

/**
 * Searches backward through the text node at `at` for `matchString`,
 * returning a Point at the start of the last occurrence before the offset.
 *
 * Only searches within the same text node (does not cross node boundaries).
 */
const findStringBeforePoint = (editor: Editor, at: Point, matchString: string): Point | undefined => {
  const [node] = Editor.node(editor, at.path);

  if (!Text.isText(node)) {
    return undefined;
  }

  const textBefore = node.text.slice(0, at.offset);
  const index = textBefore.lastIndexOf(matchString);

  if (index === -1) {
    return undefined;
  }

  return { path: [...at.path], offset: index };
};

/**
 * Checks whether the character immediately before `at` is whitespace or
 * the start of the document.
 *
 * Used to ensure autoformat delimiters only trigger at word boundaries
 * (e.g. `*bold*` triggers but `a*bold*` does not).
 */
const isPreviousCharacterEmpty = (editor: Editor, at: Point): boolean => {
  const before = Editor.before(editor, at, { unit: 'character' });

  if (before === undefined) {
    return true;
  }

  const text = Editor.string(editor, { anchor: before, focus: at });

  return text === '' || !/\S/.test(text);
};

/**
 * Locates the start and end delimiter positions within the current text
 * node, searching backward from the cursor.
 *
 * For `end`: finds the closing delimiter before the cursor.
 * For `start`: finds the opening delimiter before the closing delimiter,
 * and verifies the character before the opening delimiter is whitespace
 * or block-start.
 *
 * Returns `undefined` if any required delimiter is not found.
 */
const getMatchPoints = (editor: Editor, { end, start }: MatchRange): GetMatchPointsReturnType => {
  const selection = editor.selection;

  if (selection === null) {
    return undefined;
  }

  let beforeEndMatchPoint: Point = selection.anchor;

  if (end !== '') {
    const found = findStringBeforePoint(editor, selection.anchor, end);

    if (found === undefined) {
      return undefined;
    }

    beforeEndMatchPoint = found;
  }

  let afterStartMatchPoint: Point | undefined;
  let beforeStartMatchPoint: Point | undefined;

  if (start !== '') {
    const found = findStringBeforePoint(editor, beforeEndMatchPoint, start);

    if (found === undefined) {
      return undefined;
    }

    beforeStartMatchPoint = found;
    afterStartMatchPoint = { path: [...found.path], offset: found.offset + start.length };

    if (!isPreviousCharacterEmpty(editor, beforeStartMatchPoint)) {
      return undefined;
    }
  }

  return {
    afterStartMatchPoint,
    beforeEndMatchPoint,
    beforeStartMatchPoint,
  };
};

/**
 * Attempts to match a block autoformat rule against the current editor
 * state.
 *
 * When `triggerAtBlockStart` is true (default), the pattern is tested
 * against the entire text from the block start to the cursor. When false,
 * the pattern is matched against text immediately before the cursor.
 *
 * On match: deletes the matched text, runs `preFormat`, then either calls
 * the rule's `format` function or sets the block type via
 * `Transforms.setNodes`.
 */
const autoFormatBlock = (editor: Editor, options: AutoFormatBlockRule & { text: string }): boolean => {
  const {
    allowSameTypeAbove = false,
    format,
    match: ruleMatch,
    matchByRegex = false,
    preFormat,
    text,
    trigger,
    triggerAtBlockStart = true,
    type,
  } = options;

  const matches = toArray(ruleMatch);

  for (const match of matches) {
    const { end, triggers } = getMatchRange({ end: match, start: '' }, trigger);

    if (!triggers.includes(text)) {
      continue;
    }

    const blockEntry = Editor.above(editor, {
      match: (node) => Element.isElement(node) && Editor.isBlock(editor, node),
    });

    if (blockEntry === undefined) {
      continue;
    }

    const [, blockPath] = blockEntry;
    const blockStart = Editor.start(editor, blockPath);
    const cursorPoint = editor.selection!.anchor;

    let matchedText = '';
    let deleteRange: { anchor: Point; focus: Point } | undefined;

    if (triggerAtBlockStart) {
      const range = { anchor: blockStart, focus: cursorPoint };
      matchedText = Editor.string(editor, range);

      const isMatched = matchByRegex ? matchedText.match(end) !== null : end === matchedText;

      if (!isMatched) {
        continue;
      }

      if (match.length > 1) {
        deleteRange = range;
      }
    } else {
      const [node] = Editor.node(editor, cursorPoint.path);

      if (!Text.isText(node)) {
        continue;
      }

      const textBefore = node.text.slice(0, cursorPoint.offset);

      if (matchByRegex) {
        const regexResult = textBefore.match(new RegExp(`(${end})$`));

        if (regexResult === null || regexResult.index === undefined) {
          continue;
        }

        matchedText = regexResult[0];
        deleteRange = {
          anchor: { path: [...cursorPoint.path], offset: regexResult.index },
          focus: cursorPoint,
        };
      } else {
        if (!textBefore.endsWith(end)) {
          continue;
        }

        matchedText = end;
        deleteRange = {
          anchor: { path: [...cursorPoint.path], offset: cursorPoint.offset - end.length },
          focus: cursorPoint,
        };
      }
    }

    if (!allowSameTypeAbove && type !== undefined) {
      const [sameTypeMatch] = Editor.nodes(editor, {
        match: (node) =>
          Element.isElement(node) && 'type' in node && (node as Element & { type: string }).type === type,
      });

      if (sameTypeMatch !== undefined) {
        continue;
      }
    }

    if (deleteRange !== undefined) {
      Transforms.delete(editor, { at: deleteRange });
    }

    if (preFormat !== undefined) {
      preFormat(editor);
    }

    if (format !== undefined) {
      format(editor, { matchString: matchedText });
    } else if (type !== undefined) {
      Transforms.setNodes<Element & { type: string }>(editor, { type } as Partial<Element & { type: string }>, {
        match: (node) => Element.isElement(node) && Editor.isBlock(editor, node),
      });
    }

    return true;
  }

  return false;
};

/**
 * Attempts to match a text autoformat rule against the current editor
 * state.
 *
 * Uses {@link getMatchPoints} to locate delimiter positions, then:
 * - **function format**: calls the function with matched points.
 * - **string format**: deletes the end match and inserts the replacement.
 * - **string[] format**: replaces opening match with `format[0]` and
 *   inserts `format[1]` at the cursor.
 */
const autoFormatText = (editor: Editor, options: AutoFormatTextRule & { text: string }): boolean => {
  const { format, match: ruleMatch, text, trigger } = options;
  const selection = editor.selection!;
  const matches = toArray(ruleMatch);

  for (const match of matches) {
    const { end, start, triggers } = getMatchRange(Array.isArray(format) ? match : { end: match, start: '' }, trigger);

    if (!triggers.includes(text)) {
      continue;
    }

    const matched = getMatchPoints(editor, { end, start });

    if (matched === undefined) {
      continue;
    }

    const { afterStartMatchPoint, beforeEndMatchPoint, beforeStartMatchPoint } = matched;

    if (end !== '') {
      Transforms.delete(editor, {
        at: { anchor: beforeEndMatchPoint, focus: selection.anchor },
      });
    }

    if (typeof format === 'function') {
      format(editor, matched);
    } else {
      const formatEnd = Array.isArray(format) ? (format[1] ?? format[0] ?? '') : format;
      Transforms.insertText(editor, formatEnd);

      if (beforeStartMatchPoint !== undefined && afterStartMatchPoint !== undefined) {
        const formatStart = Array.isArray(format) ? (format[0] ?? '') : format;

        Transforms.delete(editor, {
          at: { anchor: beforeStartMatchPoint, focus: afterStartMatchPoint },
        });

        Transforms.insertText(editor, formatStart, { at: beforeStartMatchPoint });
      }
    }

    return true;
  }

  return false;
};

/**
 * Attempts to match a mark autoformat rule against the current editor
 * state.
 *
 * Uses {@link getMatchPoints} to locate opening and closing delimiters,
 * then:
 * 1. Deletes the closing delimiter.
 * 2. Selects the text between delimiters and applies the mark(s).
 * 3. Collapses the selection and clears pending marks.
 * 4. Deletes the opening delimiter.
 */
const autoFormatMark = (editor: Editor, options: AutoFormatMarkRule & { text: string }): boolean => {
  const { ignoreTrim, match: ruleMatch, text, trigger, type: markType } = options;

  if (markType.length === 0) {
    return false;
  }

  const selection = editor.selection!;
  const matchEntries = (Array.isArray(ruleMatch) ? ruleMatch : [ruleMatch]) as Array<MatchRange | string>;

  for (const match of matchEntries) {
    const { end, start, triggers } = getMatchRange(match, trigger);

    if (!triggers.includes(text)) {
      continue;
    }

    const matched = getMatchPoints(editor, { end, start });

    if (matched === undefined) {
      continue;
    }

    const { afterStartMatchPoint, beforeEndMatchPoint, beforeStartMatchPoint } = matched;

    if (afterStartMatchPoint === undefined || beforeStartMatchPoint === undefined) {
      continue;
    }

    const matchRange = { anchor: afterStartMatchPoint, focus: beforeEndMatchPoint };

    if (ignoreTrim !== true) {
      const matchText = Editor.string(editor, matchRange);

      if (matchText.trim() !== matchText) {
        continue;
      }
    }

    if (end !== '') {
      Transforms.delete(editor, {
        at: { anchor: beforeEndMatchPoint, focus: selection.anchor },
      });
    }

    const marks = toArray(markType);

    Transforms.select(editor, matchRange);
    marks.forEach((mark) => {
      Editor.addMark(editor, mark, true);
    });
    Transforms.collapse(editor, { edge: 'end' });
    marks.forEach((mark) => {
      Editor.removeMark(editor, mark);
    });

    Transforms.delete(editor, {
      at: { anchor: beforeStartMatchPoint, focus: afterStartMatchPoint },
    });

    return true;
  }

  return false;
};

/**
 * Slate plugin that enables autoformatting via `insertText` override.
 *
 * Accepts an array of {@link AutoFormatRule} definitions. On each character
 * typed (with a collapsed selection), the plugin iterates through rules and
 * dispatches to the appropriate transform based on `mode`:
 *
 * - `'block'` → {@link autoFormatBlock} — converts block type.
 * - `'text'`  → {@link autoFormatText} — inline text replacement.
 * - `'mark'`  → {@link autoFormatMark} — applies marks between delimiters.
 *
 * Multiple instances can be composed (e.g. smart quotes as a default plugin
 * plus list rules as a consumer plugin). Each instance wraps the previous
 * `insertText`, forming a chain.
 *
 * @example
 * ```ts
 * withAutoFormat([
 *   { mode: 'block', match: '# ', type: 'heading-one' },
 *   { mode: 'block', match: '- ', format: (editor) => toggleList(editor) },
 *   { mode: 'text', match: '...', format: '…' },
 *   { mode: 'mark', match: '**', type: 'bold' },
 * ])
 * ```
 */
export const withAutoFormat =
  (rules: Array<AutoFormatRule>): SlatePlugin =>
  (editor) => {
    const { insertText } = editor;

    const autoFormatEditor = editor as typeof editor & AutoFormatEditor;
    autoFormatEditor[AutoFormatSymbol] = true;

    autoFormatEditor.insertText = (text) => {
      if (autoFormatEditor.selection !== null && Range.isCollapsed(autoFormatEditor.selection)) {
        for (const rule of rules) {
          const { insertTrigger, mode = 'text', query } = rule;

          if (query !== undefined && !query(autoFormatEditor, { ...rule, text } as AutoFormatQueryOptions)) {
            continue;
          }

          let matched = false;

          if (mode === 'block') {
            matched = autoFormatBlock(autoFormatEditor, { ...(rule as AutoFormatBlockRule), text });
          } else if (mode === 'mark') {
            matched = autoFormatMark(autoFormatEditor, { ...(rule as AutoFormatMarkRule), text });
          } else {
            matched = autoFormatText(autoFormatEditor, { ...(rule as AutoFormatTextRule), text });
          }

          if (matched) {
            if (insertTrigger === true) {
              insertText(text);
            }
            return;
          }
        }
      }

      insertText(text);
    };

    return autoFormatEditor;
  };
